import { getState, setState } from '../state/store.js';
import { logout } from '../auth/auth.js';
import { getSelectedSite, openSitePicker, openAddSiteForm, isAdmin } from '../sites/sites.js';
import { openQuickAdd, wireQuickAdd, openFormSheet, closeFormSheet } from './modals.js';
import { openExpenseForm, openIncomeForm, openMaterialForm, openLabourForm } from './forms.js';
import { openUploadForm } from './uploadForm.js';
import { reportsSection, wireReportsScreen } from './reportsView.js';
import { homeSection, wireHomeScreen, homeTopBarTitleHtml, homeTopBarControlsHtml } from './homeView.js';
import { money, formatDate, todayISO, thisMonthISO } from './format.js';
import { computeTotals, filterByDate, filterByMonth, expenseCategoryBreakdown } from '../pnl/pnl.js';

let quickAddWired = false;

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[m]));
}

function txRow(tx) {
  const isIncome = tx.type === 'income';
  return `
    <div class="txRow">
      <div class="txMain">
        <b>${escapeHtml(tx.description || tx.category)}</b>
        <small>${formatDate(tx.entry_date)} · ${escapeHtml(tx.category)}${tx.payment_mode ? ' · ' + escapeHtml(tx.payment_mode) : ''}</small>
      </div>
      <div class="amount ${isIncome ? 'green' : 'red'}">${isIncome ? '+' : '−'} ${money(tx.amount)}</div>
    </div>
  `;
}

function materialRow(m) {
  return `
    <div class="txRow">
      <div class="txMain">
        <b>${escapeHtml(m.material_name)}</b>
        <small>${formatDate(m.purchase_date)} · ${m.quantity} ${escapeHtml(m.unit)} @ ${money(m.rate)}${m.supplier ? ' · ' + escapeHtml(m.supplier) : ''}${m.bill_file_path ? ' · 📎 Bill attached' : ''}</small>
        ${m.notes ? `<small>${escapeHtml(m.notes)}</small>` : ''}
      </div>
      <div class="amount red">${money(m.quantity * m.rate)}</div>
    </div>
  `;
}

function labourBasisLabel(l) {
  if (l.basis === 'daily') return `${l.days} days × ${money(l.daily_rate)}`;
  if (l.basis === 'sqft') return `${l.sqft} sqft × ${money(l.sqft_rate)}`;
  return 'Contract';
}

function labourRow(l) {
  return `
    <div class="txRow">
      <div class="txMain">
        <b>${escapeHtml(l.worker_name)}</b>
        <small>${formatDate(l.payment_date)} · ${l.basis} · ${labourBasisLabel(l)}${l.payment_mode ? ' · ' + escapeHtml(l.payment_mode) : ''}</small>
        ${l.notes ? `<small>${escapeHtml(l.notes)}</small>` : ''}
      </div>
      <div class="amount red">${money(l.amount_paid)}</div>
    </div>
  `;
}

function materialsSection(state) {
  return `
    <div class="sectionHead"><h2>Material Purchases</h2></div>
    <div class="txList">
      ${state.materialRecordsLoading ? '<div class="empty">Loading…</div>' : ''}
      ${!state.materialRecordsLoading && !state.materialRecords.length ? '<div class="empty">No material purchases yet for this site.</div>' : ''}
      ${!state.materialRecordsLoading ? state.materialRecords.map(materialRow).join('') : ''}
    </div>
  `;
}

function labourSection(state) {
  return `
    <div class="sectionHead"><h2>Labour Payments</h2></div>
    <div class="txList">
      ${state.labourRecordsLoading ? '<div class="empty">Loading…</div>' : ''}
      ${!state.labourRecordsLoading && !state.labourRecords.length ? '<div class="empty">No labour payments yet for this site.</div>' : ''}
      ${!state.labourRecordsLoading ? state.labourRecords.map(labourRow).join('') : ''}
    </div>
  `;
}

function pnlPeriodCard(title, totals, showPct) {
  return `
    <div class="pnlCard">
      <div class="pnlCardTitle">${title}</div>
      <div class="pnlStatsRow">
        <div><small>Income</small><b class="green">${money(totals.income)}</b></div>
        <div><small>Expense</small><b class="red">${money(totals.expense)}</b></div>
        <div><small>Profit</small><b class="${totals.profit >= 0 ? 'green' : 'red'}">${money(totals.profit)}</b></div>
        ${showPct ? `<div><small>Profit %</small><b>${totals.profitPct.toFixed(1)}%</b></div>` : ''}
      </div>
    </div>
  `;
}

function pnlSection(state) {
  if (state.recentTransactionsLoading) {
    return '<div class="empty">Loading…</div>';
  }
  const transactions = state.recentTransactions;
  const today = todayISO();
  const month = thisMonthISO();

  const todayTotals = computeTotals(filterByDate(transactions, today));
  const monthTotals = computeTotals(filterByMonth(transactions, month));
  const projectTotals = computeTotals(transactions);
  const breakdown = expenseCategoryBreakdown(transactions);

  return `
    ${pnlPeriodCard('Today', todayTotals, false)}
    ${pnlPeriodCard('This Month', monthTotals, false)}
    ${pnlPeriodCard('Project Total', projectTotals, true)}

    <div class="sectionHead"><h2>Expense by Category</h2></div>
    <div class="txList">
      ${!breakdown.length ? '<div class="empty">No expenses yet for this site.</div>' : ''}
      ${breakdown.map((row) => `
        <button type="button" class="catRow" data-category="${escapeHtml(row.category)}">
          <span>${escapeHtml(row.category)}</span>
          <b class="red">${money(row.amount)}</b>
        </button>
      `).join('')}
    </div>
  `;
}

const SCREENS = {
  home: { label: 'Home', icon: '⌂', render: homeSection },
  materials: { label: 'Materials', icon: '▣', render: materialsSection },
  labour: { label: 'Labour', icon: '♙', render: labourSection },
  pnl: { label: 'P&L', icon: '▤', render: pnlSection },
  reports: { label: 'Reports', icon: '▥', render: reportsSection }
};

function openCategoryTransactionsSheet(category) {
  const state = getState();
  const rows = state.recentTransactions.filter((t) => t.type === 'expense' && t.category === category);
  const body = document.createElement('div');
  body.className = 'txList';
  body.innerHTML = rows.length ? rows.map(txRow).join('') : '<div class="empty">No transactions in this category.</div>';
  openFormSheet(`${category} — Transactions`, body);
}

export function renderAppShell(root) {
  const state = getState();
  const site = getSelectedSite();
  const activeScreen = SCREENS[state.activeScreen] ? state.activeScreen : 'home';

  root.innerHTML = '';
  const shell = document.createElement('div');
  shell.className = 'appShell';

  const navButtons = Object.entries(SCREENS).map(([key, s]) =>
    `<button class="navItem${key === activeScreen ? ' active' : ''}" type="button" data-screen="${key}">${s.icon} ${s.label}</button>`
  ).join('');
  const navButtonsDisabled = `
    <button class="navItem" type="button" disabled title="Coming soon">⇄ Transactions</button>
  `;

  shell.innerHTML = `
    <aside class="sideNav">
      <div class="brand">AFIT<span>EXP</span></div>
      <nav class="navList">
        ${navButtons}
        ${navButtonsDisabled}
      </nav>
      <div class="navBottom">
        <div class="navUser"><b>${escapeHtml(state.profile?.full_name || state.session?.user?.email || '')}</b><small>${escapeHtml(state.profile?.role || '')}</small></div>
        <button class="logoutBtn" id="logoutBtn" type="button">Log out</button>
      </div>
    </aside>

    <main class="mainArea">
      <header class="topBar${activeScreen === 'home' ? ' homeTopBar' : ''}">
        ${activeScreen === 'home' ? homeTopBarTitleHtml() : `
          <button class="sitePickerTrigger" id="sitePickerTrigger" type="button">
            <small>SITE</small>
            <b>${site ? escapeHtml(site.name) : (state.sitesLoaded ? 'Select a site' : 'Loading…')}</b>
          </button>
        `}
        <div class="topBarActions">
          ${activeScreen === 'home' ? homeTopBarControlsHtml(state) : ''}
          <button class="dBtn primary" id="topAddBtn" type="button">+ Add</button>
          <button class="logoutBtnMobile" id="logoutBtnMobile" type="button">Log out</button>
        </div>
      </header>

      <section class="homeContent">
        ${!state.sitesLoaded ? '<div class="empty">Loading sites…</div>' : ''}
        ${state.sitesLoaded && activeScreen === 'home' ? SCREENS.home.render(state) : ''}
        ${state.sitesLoaded && activeScreen !== 'home' && !state.sites.length ? '<div class="empty">No sites exist yet.</div>' : ''}
        ${state.sitesLoaded && activeScreen !== 'home' && state.sites.length ? (
          state.selectedSiteId ? SCREENS[activeScreen].render(state) : '<div class="empty">Select a site to see its data.</div>'
        ) : ''}
      </section>
    </main>

    <nav class="bottomNav">
      ${Object.entries(SCREENS).map(([key, s]) =>
        `<button class="${key === activeScreen ? 'navActive' : ''}" type="button" data-screen="${key}">${s.icon}<small>${s.label}</small></button>`
      ).join('')}
      <button class="fabAdd" id="fabAdd" type="button">＋</button>
    </nav>

    ${activeScreen === 'home' && isAdmin() ? '<button type="button" class="addSiteFab" id="addSiteFab">+ Add Site</button>' : ''}
  `;

  root.appendChild(shell);

  shell.querySelectorAll('[data-screen]').forEach((btn) => {
    btn.addEventListener('click', () => setState({ activeScreen: btn.dataset.screen }));
  });

  shell.querySelectorAll('[data-category]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const category = btn.dataset.category;
      if (category === 'Materials') setState({ activeScreen: 'materials' });
      else if (category === 'Labour') setState({ activeScreen: 'labour' });
      else openCategoryTransactionsSheet(category);
    });
  });

  wireReportsScreen(shell);
  wireHomeScreen(shell);

  shell.querySelector('#sitePickerTrigger')?.addEventListener('click', openSitePicker);
  shell.querySelector('#logoutBtn').addEventListener('click', logout);
  shell.querySelector('#logoutBtnMobile').addEventListener('click', logout);
  const quickAddTrigger = () => {
    openQuickAdd((action) => {
      if (action === 'expense') openExpenseForm();
      if (action === 'income') openIncomeForm();
      if (action === 'material') openMaterialForm();
      if (action === 'labour') openLabourForm();
      if (action === 'upload') openUploadForm();
    });
  };
  shell.querySelector('#fabAdd').addEventListener('click', quickAddTrigger);
  shell.querySelector('#topAddBtn').addEventListener('click', quickAddTrigger);
  shell.querySelector('#addSiteFab')?.addEventListener('click', openAddSiteForm);

  if (!quickAddWired) {
    wireQuickAdd();
    quickAddWired = true;
  }
}
